import React from 'react';
import { Database } from 'lucide-react';
import { GenericButton } from '../../../../ui-components/Button/GenericButton';
import { REFLECTOR_DOWN_TITLE, buildReflectorDownBody } from '../../../helpers/bgpValidation';

interface ReflectorDownOverlayProps {
  /** Role + name of the down member, e.g. "Primary CTXR-5cb248". */
  memberLabel: string;
  /** The down member's last keepalive — drives the elapsed-time phrase. */
  lastUpdate?: string | number | Date | null;
  /** Label for the CTA, e.g. "View secondary data". Omit (with `onViewHealthy`) to hide it. */
  healthyMemberLabel?: string | null;
  /** Switches the cluster selector to the healthy member. Absent when every member is down. */
  onViewHealthy?: (() => void) | null;
}

/**
 * A database glyph struck through, marking the member as not reporting. lucide has no
 * `DatabaseOff`, so the slash is composed over `Database` (matching the mock and the
 * down pill in `ClusterSelector`).
 */
const DatabaseOffIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={`relative inline-flex ${className ?? ''}`} aria-hidden>
    <Database className="h-full w-full" />
    <svg viewBox="0 0 24 24" className="absolute inset-0 h-full w-full" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <line x1="3" y1="21" x2="21" y2="3" />
    </svg>
  </span>
);

/**
 * Full-canvas "reflector down" state for BGP Visibility topology (CTX-125): the graph surface
 * behind is dimmed and made non-interactive, and a centered card explains that the selected
 * member is not reporting — so a stale or empty canvas is never read as live data.
 *
 * The overlay deliberately does NOT cover the `ClusterSelector`, which sits above it in the
 * stacking order so the operator can always switch members by hand; the CTA is the one-click
 * shortcut to the healthy member and is rendered only when one exists.
 */
export const ReflectorDownOverlay: React.FC<ReflectorDownOverlayProps> = ({
  memberLabel,
  lastUpdate,
  healthyMemberLabel,
  onViewHealthy
}) => (
  <div
    // z-50 keeps this under the ClusterSelector (z-60) so member tabs stay clickable. The scrim
    // itself swallows pointer events, which is what makes the graph behind read-only.
    className="absolute inset-0 z-50 flex items-center justify-center bg-semantic-bg-primary/40 p-6"
    role="status"
    aria-live="polite"
  >
    <div className="pointer-events-auto max-w-md rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary p-6 text-center shadow-sm">
      <DatabaseOffIcon className="mx-auto mb-3 h-8 w-8 text-semantic-text-secondary" />
      <h3 className="mb-2 text-base font-semibold text-semantic-text-primary">{REFLECTOR_DOWN_TITLE}</h3>
      <p className="text-sm leading-relaxed text-semantic-text-secondary">
        {buildReflectorDownBody(memberLabel, lastUpdate)}
      </p>
      {onViewHealthy && healthyMemberLabel && (
        <GenericButton
          variant="secondary"
          size="sm"
          className="mt-4"
          label={healthyMemberLabel}
          onClick={onViewHealthy}
        />
      )}
    </div>
  </div>
);

export default ReflectorDownOverlay;
